import { Field, Formik } from 'formik'
import React from 'react'
import { Columns, Element } from 'react-bulma-components'
import useFilters from '../../hooks/filters'
import { FiltersResourcesEnum, FiltersSlice, MissionsFilters } from '../../store/filters'
import { dateInputValuesToWeekDates, dateToDateInputValue, formatISODate } from '../../utils/date'
import AutoSubmit from '../form/utils/auto-submit'
import Protected, { isAuthorizedFor } from '../protected/protected'
import CheckboxField from '../form/fields/checkbox'
import DisplayedDaysFilterField from '../filters/fields/displayed-days'
import WorkersFilterField from '../filters/fields/workers'
import ServiceFilterField from '../filters/fields/service'
import OrganizationFilterField from '../filters/fields/organization'
import { OrganizationTypeEnum } from '../../api/organizations'
import JobTitleFilterField from '../filters/fields/job-title'
import WeekFilterField from '../filters/fields/week'
import FieldColumn from '../filters/field-column'
import IsCancelledFilterField from '../filters/fields/is-cancelled'
import IsPrebilledFilterField from '../filters/fields/is-prebilled'
import { IsoDate } from 'src/hooks/use-calendar'
import FiltersWrapper from '../filters/filters-wrapper'
import EventsFilterField from '../filters/fields/events'
import WorkPeriodTagsFilterField from '../filters/fields/work-period-tags'

interface MissionFiltersProps {
  showDayFilter?: boolean
  showSummaryMinimalModeFilter?: boolean
  showEventsFilter?: boolean // TODO: remove this once we have a proper selection of fields (like in work-periods => must be generic)
  disabledQueries?: boolean
}

type MissionsFiltersFormValues = Omit<
  FiltersSlice[FiltersResourcesEnum.missions],
  'start' | 'end'
> & { week: IsoDate }

const MissionsFilters: React.FunctionComponent<MissionFiltersProps> = ({
  showDayFilter,
  showSummaryMinimalModeFilter,
  showEventsFilter,
  disabledQueries,
}) => {
  const { setFilters, filters } = useFilters(FiltersResourcesEnum.missions)

  const queriesStaleTime = 1000 * 60 * 2 // 2 min

  const { start, end, ...missionFilters } = filters

  return (
    <Formik<MissionsFiltersFormValues>
      initialValues={{
        ...missionFilters,
        week: dateToDateInputValue(filters.start),
      }}
      onSubmit={values => {
        const { week, displayDay: displayDayFormValue, ...missionFilters } = values
        const { start, end } = dateInputValuesToWeekDates(week)

        let displayDay: MissionsFiltersFormValues['displayDay'] = displayDayFormValue
        if (week !== formatISODate(filters.start)) {
          // We reset the filter on week change
          // if not, the view is stuck with an invalid displayDay value
          displayDay = undefined
        }

        if (isAuthorizedFor(['interimAgencyMember'])) {
          if (filters.employer?.length !== 1) {
            missionFilters.jobTitle = []
            missionFilters.service = []
            missionFilters.tags = []
          }
        }

        setFilters(FiltersResourcesEnum.missions, { ...missionFilters, start, end, displayDay })
      }}
      enableReinitialize
    >
      {props => {
        return (
          <FiltersWrapper
            resource={FiltersResourcesEnum.missions}
            preservedFields={['summaryMinimalMode']}
          >
            <Columns mb="0">
              <WeekFilterField />
              {showDayFilter && (
                <DisplayedDaysFilterField start={filters.start} end={filters.end} />
              )}
              <Protected roles={['employerMember', 'interimAgencyMember']}>
                {showSummaryMinimalModeFilter && (
                  <FieldColumn>
                    <Field
                      label={'Mode compact'}
                      text={<Element renderAs="small">Données résumées</Element>}
                      name="summaryMinimalMode"
                      component={CheckboxField}
                      data-test="summaryMinimalModeCheckbox"
                    />
                  </FieldColumn>
                )}
              </Protected>
            </Columns>

            <Columns mb="0">
              <Protected roles={['employerMember', 'interimAgencyMember']}>
                <WorkersFilterField disabledQueries={disabledQueries} />
              </Protected>

              <Protected roles={['interimAgencyMember']}>
                <OrganizationFilterField
                  disabledQueries={disabledQueries}
                  queriesStaleTime={queriesStaleTime}
                  type={OrganizationTypeEnum.employer}
                />
              </Protected>
              <Protected roles={['employerMember']}>
                <ServiceFilterField
                  disabledQueries={disabledQueries}
                  queriesStaleTime={queriesStaleTime}
                  employerIds={filters.employer}
                />
              </Protected>
              <Protected roles={['interimAgencyMember']}>
                <ServiceFilterField
                  disabledQueries={Boolean(disabledQueries || filters.employer?.length !== 1)}
                  queriesStaleTime={queriesStaleTime}
                  employerIds={filters.employer}
                  tooltip={
                    filters.employer?.length !== 1
                      ? 'Selectionnez un seul employeur pour pouvoir utiliser ce filtre'
                      : undefined
                  }
                />
              </Protected>
              <Protected roles={['employerMember']}>
                <OrganizationFilterField
                  disabledQueries={disabledQueries}
                  queriesStaleTime={queriesStaleTime}
                  type={OrganizationTypeEnum.interimAgency}
                />
              </Protected>
              <Protected roles={['employerMember']}>
                <JobTitleFilterField
                  disabledQueries={disabledQueries}
                  queriesStaleTime={queriesStaleTime}
                  employerIds={filters.employer}
                />
              </Protected>
              <Protected roles={['interimAgencyMember']}>
                <JobTitleFilterField
                  disabledQueries={Boolean(disabledQueries || filters.employer?.length !== 1)}
                  queriesStaleTime={queriesStaleTime}
                  employerIds={filters.employer}
                  tooltip={
                    filters.employer?.length !== 1
                      ? 'Selectionnez un seul employeur pour pouvoir utiliser ce filtre'
                      : undefined
                  }
                />
              </Protected>
              {showEventsFilter && (
                <Protected roles={['employerMember']}>
                  <EventsFilterField
                    disabledQueries={disabledQueries}
                    queriesStaleTime={queriesStaleTime}
                    labels={{
                      allAbsences: 'Avec absence',
                      allPresences: 'Avec présence',
                    }}
                  />
                </Protected>
              )}
              <Protected roles={['employerMember']}>
                <WorkPeriodTagsFilterField
                  disabledQueries={disabledQueries}
                  queriesStaleTime={queriesStaleTime}
                />
              </Protected>
              <Protected roles={['interimAgencyMember']}>
                <WorkPeriodTagsFilterField
                  disabledQueries={Boolean(disabledQueries || filters.employer?.length !== 1)}
                  queriesStaleTime={queriesStaleTime}
                  employerIds={filters.employer}
                  disabled={filters.employer?.length !== 1}
                  tooltip={
                    filters.employer?.length !== 1
                      ? 'Selectionnez un seul employeur pour pouvoir utiliser ce filtre'
                      : undefined
                  }
                />
              </Protected>
              <Protected roles={['employerMember', 'interimAgencyMember']}>
                <IsPrebilledFilterField />
                <IsCancelledFilterField />
              </Protected>
            </Columns>
            <AutoSubmit values={props.values} submitForm={props.submitForm} />
          </FiltersWrapper>
        )
      }}
    </Formik>
  )
}

export default MissionsFilters
